import { useMemo, useState } from "react";
import { BADGE_COLOR_ORDER, BADGE_COLOR_STYLE } from "../constants";
import type { BadgeColor, CellType, ColumnDef } from "../types";

interface Props {
  column: ColumnDef;
  onSave: (next: {
    type: CellType;
    options?: string[];
    optionColors?: Record<string, BadgeColor>;
  }) => void;
  onCancel: () => void;
}

const TYPE_LABELS: Record<CellType, string> = {
  text: "Text (short)",
  longText: "Text (long)",
  status: "Status (standard 6)",
  badge: "Custom dropdown",
  number: "Number",
  date: "Date",
};

const TYPE_ORDER: CellType[] = [
  "text",
  "longText",
  "status",
  "badge",
  "number",
  "date",
];

// Inline editor for an EXISTING column — change type and (for badge type)
// edit the dropdown options. Renaming happens in-place on the header itself;
// removing is its own button on the header.
//
// Type-change behavior: existing cell values are preserved verbatim. If a
// value no longer matches the new type's allowed set (e.g. badge -> status
// where the value isn't one of ALL_STATUSES), the cell still renders the
// raw value via the renderer's "unknown value" fallback.
export function EditColumnForm({ column, onSave, onCancel }: Props) {
  const [type, setType] = useState<CellType>(column.type);
  const [optionsText, setOptionsText] = useState(
    (column.options ?? []).join("\n"),
  );
  const [colors, setColors] = useState<Record<string, BadgeColor>>(
    column.optionColors ?? {},
  );

  // Colors are assigned against the options as currently typed, so the swatch
  // list updates live while the textarea is edited.
  const parsedOptions = useMemo(
    () =>
      optionsText
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
    [optionsText],
  );

  function submit() {
    const next: {
      type: CellType;
      options?: string[];
      optionColors?: Record<string, BadgeColor>;
    } = { type };
    if (type === "badge") {
      next.options = parsedOptions;
      // Drop colors for options that no longer exist so the map can't grow
      // stale entries every time someone edits the list.
      const kept: Record<string, BadgeColor> = {};
      for (const opt of parsedOptions) {
        if (colors[opt]) kept[opt] = colors[opt];
      }
      next.optionColors = kept;
    }
    onSave(next);
  }

  return (
    <div className="bg-white rounded-xl ring-1 ring-slate-200 shadow-lg p-4 w-80 space-y-3">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
        Edit column: {column.label}
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-1">Type</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as CellType)}
          className="w-full text-sm px-3 py-1.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          {TYPE_ORDER.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        {type !== column.type && (
          <p className="mt-1 text-[11px] text-amber-600">
            Type change preserves existing cell values; the renderer will fall back
            on any value that's not valid for the new type.
          </p>
        )}
      </div>
      {type === "badge" && (
        <div>
          <label className="block text-xs text-slate-500 mb-1">
            Dropdown options (one per line)
          </label>
          <textarea
            autoFocus
            value={optionsText}
            onChange={(e) => setOptionsText(e.target.value)}
            placeholder={"Option A\nOption B\nOption C"}
            rows={6}
            className="w-full text-sm px-3 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono"
          />
          <p className="mt-1 text-[11px] text-slate-400">
            Existing cells with values that aren't in this list will still display
            their original value; users can pick a different option to update.
          </p>
        </div>
      )}
      {type === "badge" && parsedOptions.length > 0 && (
        <div>
          <label className="block text-xs text-slate-500 mb-1.5">Colors</label>
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {parsedOptions.map((opt) => {
              const color = colors[opt];
              const style = color ? BADGE_COLOR_STYLE[color] : null;
              return (
                <div key={opt} className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium truncate max-w-[160px] ${
                      style
                        ? style.badge
                        : "bg-slate-100 text-slate-600 ring-1 ring-slate-200"
                    }`}
                    title={opt}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        style ? style.dot : "bg-slate-400"
                      }`}
                    />
                    {opt}
                  </span>
                  <select
                    value={color ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setColors((prev) => {
                        const next = { ...prev };
                        if (v) next[opt] = v as BadgeColor;
                        else delete next[opt];
                        return next;
                      });
                    }}
                    className="ml-auto text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="">None</option>
                    {BADGE_COLOR_ORDER.map((c) => (
                      <option key={c} value={c}>
                        {BADGE_COLOR_STYLE[c].label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          Save
        </button>
      </div>
    </div>
  );
}
